import { useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";

interface ProjectStats {
  total: number;
  active: number;
  completed: number;
  draft: number;
}

interface StatCardProps {
  title: string;
  value: number;
  onClick: () => void;
  testId?: string;
}

function StatCard({ title, value, onClick, testId }: StatCardProps) {
  return (
    <Card
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick();
        }
      }}
      className="cursor-pointer hover:shadow-lg hover:-translate-y-1 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-bauwerk-teal focus:ring-offset-2"
      data-testid={testId}
    >
      <CardContent className="p-6">
        <h3 className="text-bauwerk-teal font-medium mb-2">{title}</h3>
        <div className="text-3xl font-bold text-bauwerk-teal">{value}</div>
      </CardContent>
    </Card>
  );
}

export function ProjectStats() {
  const [, navigate] = useLocation();
  const { data: stats, isLoading } = useQuery<ProjectStats>({
    queryKey: ["/api/projects/stats"],
  });

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        {[...Array(3)].map((_, i) => (
          <Card key={i}>
            <CardContent className="p-6">
              <Skeleton className="h-4 w-24 mb-2" />
              <Skeleton className="h-8 w-16" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
      <StatCard
        title="Projekte gesamt"
        value={stats?.total || 0}
        onClick={() => navigate("/search?status=all")}
        testId="stat-card-total"
      />
      <StatCard
        title="In Bearbeitung"
        value={stats?.active || 0}
        onClick={() => navigate("/search?status=active")}
        testId="stat-card-active"
      />
      <StatCard
        title="Abgeschlossen"
        value={stats?.completed || 0}
        onClick={() => navigate("/completed")}
        testId="stat-card-completed"
      />
    </div>
  );
}
